import React, { useEffect, useRef, useState, useCallback } from 'react';
import { generateStyledQRCode, QRCodeOptions } from './qr-generator';

export interface QRCodeProps extends Omit<QRCodeOptions, 'url'> {
  url: string;
  className?: string;
  allowTilt?: boolean;
  allowGlowEffect?: boolean;
  glowColor?: string;
  width?: number;
  height?: number;
  enableShadow?: boolean;
  onLoad?: () => void;
  onError?: (error: Error) => void;
}

export default function QRCode({ 
  url,
  size = 500,
  padding = 24,
  errorCorrectionLevel = 'H',
  faviconUrl,
  dotsColor,
  qrBackground,
  glowColor = '#006bff',
  className,
  allowTilt = true,
  allowGlowEffect = true,
  width = 252,
  height = 252,
  enableShadow = false,
  onLoad,
  onError
}: QRCodeProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [qrData, setQrData] = useState<{
    modules: boolean[][];
    moduleCount: number;
    moduleSize: number;
    padding: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [tiltValues, setTiltValues] = useState({ x: 0, y: 0 });

  // Calculate proportional padding for QR generation
  const proportionalPadding = Math.max(12, size * 0.06); // 6% of QR size, minimum 12px

  // Generate QR code data
  useEffect(() => {
    const generateQR = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const qrOptions: QRCodeOptions = {
          url,
          size,
          padding: proportionalPadding,
          errorCorrectionLevel,
          faviconUrl,
          dotsColor,
          qrBackground,
          glowColor
        };
        
        const data = await generateStyledQRCode(qrOptions);
        setQrData({
          modules: data.modules,
          moduleCount: data.moduleCount,
          moduleSize: data.moduleSize,
          padding: data.padding
        });
        onLoad?.();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to generate QR code';
        setError(errorMessage);
        onError?.(err instanceof Error ? err : new Error(errorMessage));
      } finally {
        setLoading(false);
      }
    };

    generateQR();
  }, [url, size, proportionalPadding, errorCorrectionLevel, faviconUrl, dotsColor, qrBackground, glowColor, onLoad, onError]);

  // Dynamic display size based on width/height props
  const displaySize = Math.max(width, height);
  const displayPadding = Math.max(16, displaySize * 0.06); // Proportional padding
  const displayQRSize = displaySize - (displayPadding * 2);
  const displayModuleSize = qrData ? displayQRSize / qrData.moduleCount : 0;
  const displayDotRadius = displayModuleSize * 0.45; // Exact match to Mac app
  
  // Animation constants - proportional to QR size for better scaling
  const maxInnerRadius = Math.max(8, displayQRSize * 0.03); // 3% of QR size, minimum 8px
  const maxOuterRadius = Math.max(20, displayQRSize * 0.08); // 8% of QR size, minimum 20px
  
  const shouldSkipModule = (row: number, col: number): boolean => {
    if (!qrData) return true;
    
    // Skip finder pattern areas (8x8 including separator)
    const isInFinderPatternArea = 
      (row < 8 && col < 8) || // Top-left
      (row < 8 && col >= qrData.moduleCount - 8) || // Top-right
      (row >= qrData.moduleCount - 8 && col < 8); // Bottom-left

    if (isInFinderPatternArea) return true;

    // Skip center icon area (5-module radius)
    const centerRow = Math.floor(qrData.moduleCount / 2);
    const centerCol = Math.floor(qrData.moduleCount / 2);
    const iconExclusionRadius = 5.0;
    
    const rowDistance = row - centerRow;
    const colDistance = col - centerCol;
    const distanceFromCenter = Math.sqrt(rowDistance * rowDistance + colDistance * colDistance);
    
    return distanceFromCenter <= iconExclusionRadius;
  };

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!allowTilt && !allowGlowEffect) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    // Throttle updates for better performance
    if (mousePosition && Math.abs(x - mousePosition.x) < 3 && Math.abs(y - mousePosition.y) < 3) {
      return;
    }
    
    setMousePosition({ x, y });
    
    if (allowTilt) {
      // Calculate 3D tilt effects - reduced for better performance
      const centerX = displaySize / 2;
      const centerY = displaySize / 2;
      const maxTilt = 6; // degrees - reduced from 8
      
      const tiltX = ((y - centerY) / centerY) * -maxTilt;
      const tiltY = ((x - centerX) / centerX) * maxTilt;
      
      setTiltValues({ x: tiltX, y: tiltY });
    }
  }, [allowTilt, allowGlowEffect, displaySize, mousePosition]);

  const handleTouchMove = useCallback((e: React.TouchEvent<SVGSVGElement>) => {
    e.preventDefault(); // Prevent scrolling while touching QR
    if (e.touches.length !== 1) return; // Only handle single touch
    
    const rect = e.currentTarget.getBoundingClientRect();
    const touch = e.touches[0];
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    setMousePosition({ x, y });
    
    // Calculate 3D tilt effects - same as mouse
    const centerX = displaySize / 2;
    const centerY = displaySize / 2;
    const maxTilt = 8; // degrees
    
    const tiltX = ((y - centerY) / centerY) * -maxTilt;
    const tiltY = ((x - centerX) / centerX) * maxTilt;
    
    setTiltValues({ x: tiltX, y: tiltY });
  }, [displaySize]);

  const handleMouseEnter = useCallback(() => {
    setIsHovering(true);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent<SVGSVGElement>) => {
    if (e.touches.length !== 1) return; // Only handle single touch
    
    setIsHovering(true);
    // Also handle the initial touch position
    const rect = e.currentTarget.getBoundingClientRect();
    const touch = e.touches[0];
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    setMousePosition({ x, y });
    
    // Initial tilt calculation
    const centerX = displaySize / 2;
    const centerY = displaySize / 2;
    const maxTilt = 8;
    
    const tiltX = ((y - centerY) / centerY) * -maxTilt;
    const tiltY = ((x - centerX) / centerX) * maxTilt;
    
    setTiltValues({ x: tiltX, y: tiltY });
  }, [displaySize]);

  const handleMouseLeave = useCallback(() => {
    setIsHovering(false);
    setMousePosition(null);
    setTiltValues({ x: 0, y: 0 });
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsHovering(false);
    setMousePosition(null);
    setTiltValues({ x: 0, y: 0 });
  }, []);

  const getModuleColor = useCallback((row: number, col: number): string => {
    if (!isHovering || !mousePosition || !allowGlowEffect) {
      // Use custom dots color if provided, otherwise use light theme default
      if (dotsColor) {
        return dotsColor;
      }
      return '#000000';
    }

    const dotX = displayPadding + (col * displayModuleSize) + (displayModuleSize / 2);
    const dotY = displayPadding + (row * displayModuleSize) + (displayModuleSize / 2);
    
    const dx = dotX - mousePosition.x;
    const dy = dotY - mousePosition.y;
    const distanceSquared = dx * dx + dy * dy;
    
    // Use squared distance for better performance (avoid sqrt)
    const maxInnerSquared = maxInnerRadius * maxInnerRadius;
    const maxOuterSquared = maxOuterRadius * maxOuterRadius;
    
    if (distanceSquared <= maxInnerSquared) {
      // Core Zone: Invisible dots (use background color)
      return qrBackground || '#ffffff';
    } else if (distanceSquared <= maxOuterSquared) {
      // Gradient Halo Zone - simplified interpolation
      const t = (distanceSquared - maxInnerSquared) / (maxOuterSquared - maxInnerSquared);
      const smoothT = Math.max(0, Math.min(1, t));
      
      // Use custom glow color if provided
      let hoverColor: string;
      if (glowColor) {
        hoverColor = glowColor;
      } else {
        // Default to Geist Blue 700
        hoverColor = '#006bff';
      }
      
      // Parse the hover color
      const hoverR = parseInt(hoverColor.slice(1, 3), 16);
      const hoverG = parseInt(hoverColor.slice(3, 5), 16);
      const hoverB = parseInt(hoverColor.slice(5, 7), 16);
      
      // Normal color (dots color or light theme default)
      let normalColor: string;
      if (dotsColor) {
        normalColor = dotsColor;
      } else {
        normalColor = '#000000';
      }
      
      const normalR = parseInt(normalColor.slice(1, 3), 16);
      const normalG = parseInt(normalColor.slice(3, 5), 16);
      const normalB = parseInt(normalColor.slice(5, 7), 16);
      
      const r = Math.round(hoverR + (normalR - hoverR) * smoothT);
      const g = Math.round(hoverG + (normalG - hoverG) * smoothT);
      const b = Math.round(hoverB + (normalB - hoverB) * smoothT);
      
      return `rgb(${r}, ${g}, ${b})`;
    }
    
    // Use custom dots color if provided, otherwise use light theme default
    if (dotsColor) {
      return dotsColor;
    }
    return '#000000';
  }, [isHovering, mousePosition, allowGlowEffect, dotsColor, displayPadding, displayModuleSize, maxInnerRadius, maxOuterRadius, glowColor, displayQRSize]);

  const getModuleRadius = useCallback((row: number, col: number): number => {
    if (!isHovering || !mousePosition || !allowGlowEffect) {
      return displayDotRadius;
    }

    const dotX = displayPadding + (col * displayModuleSize) + (displayModuleSize / 2);
    const dotY = displayPadding + (row * displayModuleSize) + (displayModuleSize / 2);
    
    const dx = dotX - mousePosition.x;
    const dy = dotY - mousePosition.y;
    const distanceSquared = dx * dx + dy * dy;
    
    // Use squared distance for better performance
    const maxInnerSquared = maxInnerRadius * maxInnerRadius;
    const maxOuterSquared = maxOuterRadius * maxOuterRadius;
    
    const minDotRadius = displayDotRadius * 0.2;
    
    if (distanceSquared <= maxInnerSquared) {
      return minDotRadius;
    } else if (distanceSquared <= maxOuterSquared) {
      const t = (distanceSquared - maxInnerSquared) / (maxOuterSquared - maxInnerSquared);
      const smoothT = Math.max(0, Math.min(1, t));
      return minDotRadius + (displayDotRadius - minDotRadius) * smoothT;
    }
    
    return displayDotRadius;
  }, [isHovering, mousePosition, allowGlowEffect, displayPadding, displayModuleSize, displayDotRadius, maxInnerRadius, maxOuterRadius, displayQRSize]);

  const renderFinderPattern = (x: number, y: number) => {
    const outerSize = displayModuleSize * 7;
    const outerCornerRadius = displayModuleSize * 2.0;
    const middleSize = displayModuleSize * 5;
    const middleCornerRadius = displayModuleSize * 1.5;
    const innerSize = displayModuleSize * 3;
    const innerCornerRadius = displayModuleSize * 0.8;
    
    // Use custom colors if provided, otherwise use light theme defaults
    const outerColor = dotsColor || '#000000';
    const innerColor = qrBackground || '#ffffff';
    const centerColor = dotsColor || '#000000';
    
    // Check if this finder pattern should have glow effect
    const shouldGlow = allowGlowEffect && isHovering && mousePosition;
    
    return (
      <g key={`finder-${x}-${y}`} className={shouldGlow ? "transition-all duration-150 ease-out" : ""}>
        {/* Outer squircle */}
        <rect
          x={x}
          y={y}
          width={outerSize}
          height={outerSize}
          rx={outerCornerRadius}
          ry={outerCornerRadius}
          fill={outerColor}
        />
        {/* Middle squircle */}
        <rect
          x={x + displayModuleSize}
          y={y + displayModuleSize}
          width={middleSize}
          height={middleSize}
          rx={middleCornerRadius}
          ry={middleCornerRadius}
          fill={innerColor}
        />
        {/* Inner squircle */}
        <rect
          x={x + (displayModuleSize * 2)}
          y={y + (displayModuleSize * 2)}
          width={innerSize}
          height={innerSize}
          rx={innerCornerRadius}
          ry={innerCornerRadius}
          fill={centerColor}
        />
      </g>
    );
  };

  const renderCenterIcon = () => {
    const centerX = displayPadding + (displayQRSize / 2);
    const centerY = displayPadding + (displayQRSize / 2);
    const iconSize = displayDotRadius * 2 * 8; // 8x the dot diameter like Mac app
    const iconRadius = iconSize / 2;
    
    const strokeColor = 'rgba(0, 0, 0, 1.0)';
    
    return (
      <g>
        {/* Favicon if available */}
        {faviconUrl && (
          <image
            href={faviconUrl}
            x={centerX - iconRadius * 0.7}
            y={centerY - iconRadius * 0.7}
            width={iconRadius * 1.4}
            height={iconRadius * 1.4}
            preserveAspectRatio="xMidYMid meet"
          />
        )}
        
        {/* Fallback globe icon if no favicon */}
        {!faviconUrl && (
          <>
            {/* Globe lines */}
            <g stroke={strokeColor} strokeWidth={displayDotRadius * 2} fill="none">
              {/* Main circle border */}
              <circle cx={centerX} cy={centerY} r={iconRadius} />
              
              {/* Horizontal line (equator) */}
              <line x1={centerX - iconRadius} y1={centerY} x2={centerX + iconRadius} y2={centerY} />
              
              {/* Vertical ellipse (longitude) */}
              <ellipse cx={centerX} cy={centerY} rx={iconRadius * 0.6} ry={iconRadius} />
            </g>
          </>
        )}
      </g>
    );
  };

  if (loading) {
    return (
      <div 
        className={`flex items-center justify-center ${className || ''}`}
        style={{ width, height }}
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error || !qrData) {
    return (
      <div 
        className={`flex items-center justify-center text-red-500 ${className || ''}`}
        style={{ width, height }}
      >
        {error || 'Failed to generate QR code'}
      </div>
    );
  }

  return (
    <svg
      ref={svgRef}
      width={width}
      height={height}
      viewBox={`0 0 ${displaySize} ${displaySize}`}
      onMouseMove={allowTilt || allowGlowEffect ? handleMouseMove : undefined}
      onMouseEnter={allowTilt || allowGlowEffect ? handleMouseEnter : undefined}
      onMouseLeave={allowTilt || allowGlowEffect ? handleMouseLeave : undefined}
      onTouchMove={allowTilt || allowGlowEffect ? handleTouchMove : undefined}
      onTouchStart={allowTilt || allowGlowEffect ? handleTouchStart : undefined}
      onTouchEnd={allowTilt || allowGlowEffect ? handleTouchEnd : undefined}
      className={`block cursor-pointer transition-transform duration-150 ease-out ${className || ''}`}
      style={{ 
        width: width, 
        height: height,
        borderRadius: '4px',
        transform: allowTilt ? `perspective(1000px) rotateX(${tiltValues.x}deg) rotateY(${tiltValues.y}deg)` : 'none',
        boxShadow: enableShadow ? '0 25px 50px -12px rgba(0, 0, 0, 0.25)' : 'none',
      } as React.CSSProperties}
    >
      {/* Background */}
      <rect 
        width={displaySize} 
        height={displaySize} 
        fill={qrBackground || '#ffffff'} 
        rx="4" 
        ry="4" 
      />
      
      {/* Data modules */}
      {qrData.modules.map((row, rowIndex) =>
        row.map((isSet, colIndex) => {
          if (!isSet || shouldSkipModule(rowIndex, colIndex)) return null;
          
          const x = displayPadding + (colIndex * displayModuleSize) + (displayModuleSize / 2);
          const y = displayPadding + (rowIndex * displayModuleSize) + (displayModuleSize / 2);
          const color = getModuleColor(rowIndex, colIndex);
          const radius = getModuleRadius(rowIndex, colIndex);
          
          return (
            <circle
              key={`${rowIndex}-${colIndex}`}
              cx={x}
              cy={y}
              r={radius}
              fill={color}
              className="transition-all duration-150 ease-out"
            />
          );
        })
      )}
      
      {/* Finder patterns */}
      {renderFinderPattern(displayPadding, displayPadding)} {/* Top-left */}
      {renderFinderPattern(displayPadding + displayQRSize - (displayModuleSize * 7), displayPadding)} {/* Top-right */}
      {renderFinderPattern(displayPadding, displayPadding + displayQRSize - (displayModuleSize * 7))} {/* Bottom-left */}
      
      {/* Center icon */}
      {renderCenterIcon()}
    </svg>
  );
}
